import { useState } from "react";
import { DataWrapper } from "@/common/components/dataWrapper";
import Form from "@/common/components/form/Form";
import Stepper from "@/common/components/stepper";
import { KeyValueObj } from "@/common/types";
import { inventoryStep1Form } from "./data";
import { BULK_UPLOAD_NUDGE_THRESHOLD } from "./serialAccelerators";

const INVENTORY_STEPS = [
  { id: 1, label: "Batch details" },
  { id: 2, label: "Serial entry" },
];

interface IInventoryStep1Props {
  productOptions: KeyValueObj[];
  productsFetched: KeyValueObj[];
  productsLoading: boolean;
  productsError: any;
  onRetry: () => void;
  onContinue: (values: KeyValueObj, product: KeyValueObj) => void;
  onSwitchToBulkUpload: (productId: string) => void;
  onDownloadTemplate: (productId: string) => void;
  templateLoading: boolean;
}

// Step 1 of the MED-649 complete-batch inventory flow: batch details, plus a
// product-details/hierarchy summary and a bulk-upload nudge, both shown once
// a product is selected — before the form fields below them.
export function InventoryStep1({
  productOptions,
  productsFetched,
  productsLoading,
  productsError,
  onRetry,
  onContinue,
  onSwitchToBulkUpload,
  onDownloadTemplate,
  templateLoading,
}: IInventoryStep1Props) {
  const [selectedProductId, setSelectedProductId] = useState("");

  const selectedProduct = productsFetched?.find((p) => p.id === selectedProductId);
  const layers: KeyValueObj[] = selectedProduct?.packaging_layers || [];
  const totalSerials: number = selectedProduct
    ? (selectedProduct.base_unit_count || 0) + Math.max(layers.length - 1, 0)
    : 0;
  const suggestBulkUpload = totalSerials > BULK_UPLOAD_NUDGE_THRESHOLD;

  function handleFieldChange(name: string, value: any) {
    if (name === "product_id") setSelectedProductId(value);
  }

  function handleContinue(values: KeyValueObj) {
    const product = productsFetched?.find((p) => p.id === values.product_id);
    if (!product?.packaging_layers?.length) return;
    onContinue(values, product);
  }

  const formInputs = [
    {
      inputType: "selectInput",
      prompt: "Select the product",
      name: "product_id",
      placeholder: "Select a product from the list",
      fullWidth: false,
      returnId: true,
      options: productOptions,
    },
    ...inventoryStep1Form,
  ];

  return (
    <DataWrapper
      noDataMessage="No product added yet, Products added will show here."
      loading={productsLoading}
      error={productsError}
      onFailAction={onRetry}
      loadingMessage="Loading products data..."
      failedMessage="Could not fetch products data. Check your network and click the button below to retry"
    >
      {/* A single wrapping div, not a fragment: ModalFormWrapper is a flex
          column with a large `gap` between DIRECT children, and a fragment
          is transparent to that — it would apply the gap between the
          stepper, hierarchy card, and form individually instead of once
          around this whole block. The div also gets an explicit width since
          ModalFormWrapper has align-items: center (no stretch), which would
          otherwise shrink it to fit its content. */}
      <div className="w-full">
        <div className="mb-3 w-full">
          <Stepper steps={INVENTORY_STEPS} currentStep={0} />
        </div>

        {selectedProduct && layers.length > 0 && (
          <div className="mb-3 w-full rounded-lg border border-blue-100 bg-blue-50 p-4">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <span className="text-sm font-semibold text-gray-800">
                {selectedProduct.brand_name}
                {selectedProduct.strength ? ` — ${selectedProduct.strength}${selectedProduct.strength_unit === "grams" ? "mg" : "g"}` : ""}
              </span>
              <span className="whitespace-nowrap text-xs font-medium text-blue-700">
                {totalSerials} serial{totalSerials === 1 ? "" : "s"} needed
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
              {layers.map((layer, idx) => (
                <span key={idx} className="flex items-center gap-1.5">
                  <span className="rounded-full bg-white px-2 py-0.5 font-medium capitalize text-gray-700 shadow-sm">
                    {idx === 0 ? "1 " : ""}
                    {layer.packaging_type}
                    {layer.containment_quantity ? ` (${layer.containment_quantity})` : ""}
                  </span>
                  {idx < layers.length - 1 && <span className="text-gray-400">→</span>}
                </span>
              ))}
            </div>

            {suggestBulkUpload && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                <span>
                  This batch needs {totalSerials} serials — bulk file upload may be faster for a batch this size.
                </span>
                <div className="flex shrink-0 gap-3">
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => onDownloadTemplate(selectedProduct.id)}
                    disabled={templateLoading}
                  >
                    {templateLoading ? "Preparing..." : "Download template"}
                  </button>
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => onSwitchToBulkUpload(selectedProduct.id)}
                  >
                    Switch to bulk upload
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <Form
          loadingState={false}
          submitLabel="Continue"
          formInputs={formInputs}
          processInputs={handleContinue}
          spinnerComponent="spinner"
          login={false}
          biControl={false}
          handleBack={undefined}
          externalUpdateData={handleFieldChange}
        />
      </div>
    </DataWrapper>
  );
}
